import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { supabase, authClient } from '../lib/supabase';
import { clearBillingSnapshot } from '../lib/billingSnapshot';
import { reportDeviceSeen } from '../lib/deviceSeen';
import { trackRegisterCompleted } from '../lib/gaEvents';
import type { User, Session } from '@supabase/supabase-js';

// SDK storage key (must match supabase.ts storageKey)
const SDK_SESSION_KEY = 'filmple-auth';

// LocalStorage keys
const AUTH_CACHE_KEY = 'filmple:auth';

// Cached auth data structure
interface CachedAuthData {
  user: User;
  profile: Profile;
  timestamp: number;
}

// Cache expiry: 7 days (we verify in background anyway)
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

// Retry backoff delays (ms) for session/profile recovery
const RETRY_DELAYS_MS = [250, 500];

// Structured auth log for observability
function authLog(
  level: 'warn' | 'error',
  operation: string,
  details: { attempt?: number; timeoutMs?: number; durationMs?: number; error?: unknown } & Record<string, unknown>,
) {
  const payload = {
    operation,
    ...details,
    online: navigator.onLine,
    visibility: document.visibilityState,
    error: details.error instanceof Error ? details.error.message : details.error ? String(details.error) : undefined,
  };
  if (level === 'error') {
    console.error(`[Auth] ${operation} failed (final)`, payload);
  } else {
    console.warn(`[Auth] ${operation} attempt failed`, payload);
  }
}

// Helper to get cached auth from localStorage
function getCachedAuth(): CachedAuthData | null {
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    if (!cached) return null;

    const data = JSON.parse(cached) as CachedAuthData;

    // Check if cache is expired
    if (Date.now() - data.timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }

    // Preload avatar immediately on cache read
    preloadAvatarImage(data.profile?.avatar_url);

    return data;
  } catch {
    return null;
  }
}

// Helper to preload avatar image into browser cache
function preloadAvatarImage(avatarUrl: string | null): void {
  if (!avatarUrl) return;
  const img = new Image();
  img.src = avatarUrl;
}

// Helper to save auth to localStorage
function setCachedAuth(user: User, profile: Profile): void {
  try {
    const data: CachedAuthData = { user, profile, timestamp: Date.now() };
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(data));
    // Preload avatar into browser cache
    preloadAvatarImage(profile.avatar_url);
  } catch (err) {
    console.error('[Auth] Failed to cache auth:', err);
  }
}

// Helper to clear cached auth
function clearCachedAuth(): void {
  try {
    localStorage.removeItem(AUTH_CACHE_KEY);
  } catch {
    // Ignore errors
  }
}

function markAccountClosedNotice(): void {
  try {
    sessionStorage.setItem('filmple_account_closed_notice', '1');
  } catch {
    // Ignore storage failures
  }
}

/**
 * Read the Supabase SDK session directly from localStorage.
 * The SDK stores a full Session object under the `filmple-auth` key.
 * Returns { session: null } if missing, expired, or malformed.
 */
function readStoredSession(): { session: Session | null } {
  try {
    const raw = localStorage.getItem(SDK_SESSION_KEY);
    if (!raw) return { session: null };

    const parsed = JSON.parse(raw);
    // Supabase JS v2 stores the session directly (not nested)
    const session = parsed as Session;

    // Defensive shape checks
    if (
      typeof session.access_token !== 'string' || !session.access_token ||
      typeof session.expires_at !== 'number' ||
      typeof session.user?.id !== 'string' || !session.user.id
    ) {
      return { session: null };
    }

    // Reject sessions within 30s of expiry — SDK would need to refresh, so don't bootstrap
    // with a nearly-stale token. SDK's own margin is 90s, so this leaves a 31-89s window
    // where bootstrap works but SDK refreshes in background (safe: fetch timeout bounds it).
    const BOOTSTRAP_EXPIRY_MARGIN_S = 30;
    if (session.expires_at <= Math.floor(Date.now() / 1000) + BOOTSTRAP_EXPIRY_MARGIN_S) {
      return { session: null };
    }

    return { session };
  } catch {
    return { session: null };
  }
}

/** Add random jitter (0-50% of base) to retry delays to prevent thundering herd. */
function jitteredDelay(baseMs: number): number {
  return baseMs + Math.floor(Math.random() * baseMs * 0.5);
}

// Profile visibility type (3-tier system)
export type ProfileVisibility = 'public' | 'friends' | 'private';

// Test pro mode type (test users can override their tier)
// Bounded to DB CHECK constraint: profiles_test_pro_mode_check ('auto','free','social','pro')
export type TestProMode = 'auto' | 'free' | 'social' | 'pro';

// Profile type matching Supabase profiles table
export interface Profile {
  id: string;
  username: string;
  username_lower: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  website_url: string | null;
  is_public: boolean; // Legacy field, kept for backwards compatibility
  visibility: ProfileVisibility; // New 3-tier visibility field
  onboarding_completed: boolean;
  is_pro: boolean; // Pro subscription status (Phase 2.1 AI paywall gating)
  is_test_user: boolean; // Test user can toggle between Free/Social/Pro tiers
  test_pro_mode: TestProMode; // 'auto' uses real tier, 'free'/'social'/'pro' overrides (DB bounded)
  subscription_tier?: string; // Canonical tier: 'free' | 'social' | 'pro' (Phase 4)
  billing_source?: string; // 'manual' | 'test' | 'stripe' | 'revenuecat'
  entitlement_expires_at?: string | null; // ISO timestamp or null
  activity_visibility_enabled?: boolean; // Privacy: show activity to friends (default true)
  allow_recommendations?: 'followers' | 'none'; // Privacy: who can send recs (default 'followers')
  show_in_search?: boolean; // Privacy: appear in user search (default true)
  current_mood_status?: string | null; // Optional live mood status for recs/chat context
  deleted_at: string | null; // Account deactivation timestamp
  is_suspended?: boolean; // Account suspended by admin
  chat_messages_this_month: number | null; // Usage counter for membership UI
  chat_month: string | null; // Month bucket for usage counter (YYYY-MM)
  created_at: string;
  updated_at: string;
}

// Profile fetch status - distinguishes between loading, success, confirmed-missing, and error
export type ProfileStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

// Discriminated result from fetchProfile
type ProfileFetchResult =
  | { status: 'ready'; profile: Profile }
  | { status: 'missing' }
  | { status: 'error'; error: Error };

// Fields that can be updated via updateProfile
export interface ProfileUpdateFields {
  display_name?: string | null;
  bio?: string | null;
  location?: string | null;
  is_public?: boolean; // Legacy field
  visibility?: ProfileVisibility; // New 3-tier visibility field
  avatar_url?: string | null;
  current_mood_status?: string | null;
  test_pro_mode?: TestProMode; // Test users can update their tier override
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;
  profileStatus: ProfileStatus;
  profileError: Error | null;
  needsUsername: boolean;
  needsOnboarding: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  setProfileUsername: (username: string, seededFields?: { display_name?: string | null; avatar_url?: string | null }) => void;
  refreshProfile: () => Promise<void>;
  updateProfile: (fields: ProfileUpdateFields) => Promise<{ error: Error | null }>;
  completeOnboarding: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Initialize from BOTH localStorage sources for instant display:
  // 1. filmple:auth (app cache: User + Profile) — fast but may be stale
  // 2. filmple-auth (SDK session) — has access_token needed by downstream guards
  let cachedAuth = getCachedAuth();
  const stored = readStoredSession();

  // Cross-user guard: if user IDs differ, filmple:auth is stale (e.g. logged in as different user)
  if (cachedAuth && stored.session && cachedAuth.user.id !== stored.session.user.id) {
    clearCachedAuth();
    cachedAuth = null;
  }

  const [user, setUser] = useState<User | null>(cachedAuth?.user ?? stored.session?.user ?? null);
  const [session, setSession] = useState<Session | null>(stored.session);
  const [profile, setProfile] = useState<Profile | null>(cachedAuth?.profile ?? null);
  // If we have cached auth OR stored session, don't show loading - verify in background
  const [loading, setLoading] = useState(!cachedAuth && !stored.session);
  // Track if we've completed initial auth check
  // If we have cached auth OR stored session, mark as initialized immediately
  const [initialized, setInitialized] = useState(!!cachedAuth || !!stored.session);
  // Deterministic profile status: idle → loading → ready|missing|error
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>(
    cachedAuth?.profile ? 'ready' : (stored.session ? 'loading' : 'idle')
  );
  const [profileError, setProfileError] = useState<Error | null>(null);

  // Refs to track current state for use in event listener closures (avoids stale state)
  const profileRef = useRef<Profile | null>(cachedAuth?.profile ?? null);
  const userRef = useRef<User | null>(cachedAuth?.user ?? stored.session?.user ?? null);
  const sessionRef = useRef<Session | null>(stored.session);

  // Single-flight deduplication for profile fetches
  const inFlightProfileFetchRef = useRef<Map<string, Promise<ProfileFetchResult>>>(new Map());

  // Track if we've accepted a session (for INITIAL_SESSION gating)
  const hasAcceptedSessionRef = useRef(false);

  // Background session recovery: set when getSession retries exhausted with cached data
  const needsSessionRecoveryRef = useRef(false);
  const recoveryInFlightRef = useRef(false);

  // Keep refs in sync with state for use in onAuthStateChange closure
  useEffect(() => { profileRef.current = profile; }, [profile]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Fetch user profile from Supabase with timeout and single-flight deduplication
  // Returns discriminated result: ready (profile found), missing (confirmed no row), error (network/timeout)
  const fetchProfile = useCallback(async (userId: string, signal?: AbortSignal): Promise<ProfileFetchResult> => {
    // If this caller is already aborted, bail early
    if (signal?.aborted) return { status: 'error', error: new Error('Aborted') };

    // Check if there's already an in-flight request for this userId
    const existing = inFlightProfileFetchRef.current.get(userId);
    if (existing) {
      // Wait for existing request, but respect abort signal
      if (!signal) return existing;
      return Promise.race([
        existing,
        new Promise<ProfileFetchResult>((resolve) => {
          signal.addEventListener('abort', () => resolve({ status: 'error', error: new Error('Aborted') }), { once: true });
        }),
      ]);
    }

    const profileFetchPromise = (async (): Promise<ProfileFetchResult> => {
      // Per-attempt AbortController: aborts the actual Supabase HTTP request on timeout
      const attemptController = new AbortController();
      const TIMEOUT_MS = 8000;
      const timeoutId = setTimeout(() => attemptController.abort(), TIMEOUT_MS);

      // Link parent signal to attempt abort
      const onParentAbort = () => attemptController.abort();
      signal?.addEventListener('abort', onParentAbort, { once: true });

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .abortSignal(attemptController.signal)
          .maybeSingle();

        clearTimeout(timeoutId);
        signal?.removeEventListener('abort', onParentAbort);
        if (signal?.aborted) return { status: 'error', error: new Error('Aborted') };

        if (error) {
          console.error('[Auth] Failed to fetch profile:', error);
          return { status: 'error', error: error as Error };
        }

        // No error AND no data = confirmed missing row (new user, no profile yet)
        if (!data) return { status: 'missing' };

        return {
          status: 'ready',
          profile: {
            ...data,
            is_public: data.is_public ?? true,
            visibility: data.visibility ?? 'public',
            onboarding_completed: data.onboarding_completed ?? false,
            is_pro: data.is_pro ?? false,
            is_test_user: data.is_test_user ?? false,
            test_pro_mode: data.test_pro_mode ?? 'auto',
            activity_visibility_enabled: data.activity_visibility_enabled ?? true,
            allow_recommendations: data.allow_recommendations ?? 'followers',
            show_in_search: data.show_in_search ?? true,
            deleted_at: data.deleted_at ?? null,
            chat_messages_this_month: data.chat_messages_this_month ?? null,
            chat_month: data.chat_month ?? null,
          } as Profile,
        };
      } catch (err) {
        clearTimeout(timeoutId);
        signal?.removeEventListener('abort', onParentAbort);
        if (err instanceof Error && (err.message === 'Aborted' || err.name === 'AbortError')) {
          return { status: 'error', error: new Error('Profile fetch timeout') };
        }
        console.error('[Auth] Profile fetch error:', err);
        return { status: 'error', error: err instanceof Error ? err : new Error('Profile fetch failed') };
      } finally {
        inFlightProfileFetchRef.current.delete(userId);
      }
    })();

    inFlightProfileFetchRef.current.set(userId, profileFetchPromise);

    if (!signal) return profileFetchPromise;
    return Promise.race([
      profileFetchPromise,
      new Promise<ProfileFetchResult>((resolve) => {
        signal.addEventListener('abort', () => resolve({ status: 'error', error: new Error('Aborted') }), { once: true });
      }),
    ]);
  }, []);

  // Handle suspended profile — sign out and dispatch closed-account event
  const handleSuspendedProfile = useCallback(async () => {
    console.log('[Auth] Profile is suspended — signing out');
    clearCachedAuth();
    clearBillingSnapshot();
    markAccountClosedNotice();
    try { await authClient.auth.signOut(); } catch {}
    setUser(null);
    setSession(null);
    setProfile(null);
    setProfileStatus('idle');
    window.dispatchEvent(new CustomEvent('auth:account-closed'));
  }, []);

  // Refresh profile from database (cache-safe: never bricks cached users)
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const hadCachedProfile = !!profileRef.current;
    if (!hadCachedProfile) {
      setProfileStatus('loading');
    }
    const result = await fetchProfile(user.id);
    if (result.status === 'ready') {
      if (result.profile.is_suspended) {
        await handleSuspendedProfile();
        return;
      }
      setProfile(result.profile);
      setProfileStatus('ready');
      setProfileError(null);
      setCachedAuth(user, result.profile);
    } else if (result.status === 'missing') {
      setProfile(null);
      setProfileStatus('missing');
      setProfileError(null);
      clearCachedAuth();
    } else {
      // Only brick UI if no cached profile; otherwise keep UI usable
      if (hadCachedProfile) {
        setProfileStatus('ready');
      } else {
        setProfileStatus('error');
      }
      setProfileError(result.error);
    }
  }, [user, fetchProfile]);

  // Set profile username after UsernameModal completes.
  // Accepts optional seeded fields from the server response so we don't
  // overwrite display_name / avatar_url that were seeded from auth metadata.
  const setProfileUsername = useCallback((username: string, seededFields?: { display_name?: string | null; avatar_url?: string | null }) => {
    if (user) {
      setProfile(prev => {
        const newProfile: Profile = prev
          ? {
              ...prev,
              username,
              username_lower: username.toLowerCase(),
              // Only merge seeded fields if non-null (don't overwrite existing values with null)
              ...(seededFields?.display_name != null ? { display_name: seededFields.display_name } : {}),
              ...(seededFields?.avatar_url != null ? { avatar_url: seededFields.avatar_url } : {}),
            }
          : {
              id: user.id,
              username,
              username_lower: username.toLowerCase(),
              display_name: seededFields?.display_name ?? null,
              avatar_url: seededFields?.avatar_url ?? null,
              bio: null,
              location: null,
              website_url: null,
              is_public: true,
              visibility: 'public' as ProfileVisibility,
              onboarding_completed: false,
              is_pro: false,
              is_test_user: false,
              test_pro_mode: 'auto' as TestProMode,
              activity_visibility_enabled: true,
              allow_recommendations: 'followers' as const,
              show_in_search: true,
              deleted_at: null,
              chat_messages_this_month: null,
              chat_month: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
        setCachedAuth(user, newProfile);
        return newProfile;
      });
      setProfileStatus('ready');
      setProfileError(null);
      trackRegisterCompleted();
    }
  }, [user]);

  // Mark onboarding as complete (updates local state)
  const completeOnboarding = useCallback(() => {
    setProfile(prev => prev ? { ...prev, onboarding_completed: true } : null);
  }, []);

  // Update profile fields
  const updateProfile = useCallback(async (fields: ProfileUpdateFields): Promise<{ error: Error | null }> => {
    if (!user) {
      return { error: new Error('Not authenticated') };
    }

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          ...fields,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('[Auth] Profile update failed:', updateError);
        return { error: updateError as Error };
      }

      // Update local state AND localStorage cache atomically
      setProfile(prev => {
        if (!prev) return null;
        const updatedProfile = { ...prev, ...fields, updated_at: new Date().toISOString() };
        // Keep localStorage cache in sync for instant boot on refresh
        setCachedAuth(user, updatedProfile);
        return updatedProfile;
      });

      return { error: null };
    } catch (err) {
      console.error('[Auth] Profile update error:', err);
      return { error: err as Error };
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;
    const abortController = new AbortController();

    // Get initial session + profile with retry/backoff
    const initAuth = async () => {
      try {
        const isBootstrapped = !!sessionRef.current;

        // --- Bootstrapped path: skip getSession() entirely ---
        // localStorage has a valid session (set in useState initializers).
        // authClient's autoRefreshToken + onAuthStateChange handle session lifecycle.
        if (isBootstrapped) {
          hasAcceptedSessionRef.current = true;

          // Verify the auth user still exists before trusting the cached session.
          // Cached localStorage sessions can reference deleted users — if getUser()
          // fails authoritatively, sign out to clear stale state.
          if (userRef.current) {
            try {
              const { data: { user: verifiedUser }, error: verifyError } = await authClient.auth.getUser();
              if (verifyError || !verifiedUser) {
                const msg = verifyError?.message?.toLowerCase() || '';
                const isAuthoritative = msg.includes('session') || msg.includes('not found')
                  || msg.includes('invalid') || msg.includes('missing');
                if (isAuthoritative) {
                  console.warn('[Auth] Cached session invalid (user deleted?) — signing out:', verifyError?.message);
                  clearCachedAuth();
                  await authClient.auth.signOut();
                  return;
                }
                // Non-authoritative (network error) — proceed with cached session
                console.warn('[Auth] Auth user verification failed (non-fatal):', verifyError?.message);
              }
            } catch (verifyErr) {
              // Network error — don't sign out, proceed with cached session
              console.warn('[Auth] Auth user verification threw (non-fatal):', verifyErr);
            }
          }

          // Background profile fetch with retry (non-blocking)
          const hadCachedProfile = !!profileRef.current;
          if (!hadCachedProfile && userRef.current) {
            setProfileStatus('loading');
          }

          if (userRef.current) {
            const userId = userRef.current.id;
            const MAX_PROFILE_RETRIES = 3;
            (async () => {
              for (let attempt = 1; attempt <= MAX_PROFILE_RETRIES; attempt++) {
                if (!mounted) return;
                const start = Date.now();
                const result = await fetchProfile(userId, abortController.signal);

                if (result.status === 'ready') {
                  if (!mounted) return;
                  if (result.profile.is_suspended) {
                    await handleSuspendedProfile();
                    return;
                  }
                  setProfile(result.profile);
                  setProfileStatus('ready');
                  setProfileError(null);
                  if (userRef.current) setCachedAuth(userRef.current, result.profile);
                  return;
                }

                if (result.status === 'missing') {
                  if (!mounted) return;
                  setProfile(null);
                  setProfileStatus('missing');
                  setProfileError(null);
                  clearCachedAuth();
                  return;
                }

                // Error — log and retry
                authLog('warn', 'fetchProfile', {
                  attempt,
                  timeoutMs: 8000,
                  durationMs: Date.now() - start,
                  error: result.error,
                });

                if (attempt < MAX_PROFILE_RETRIES) {
                  await new Promise(r => setTimeout(r, jitteredDelay(RETRY_DELAYS_MS[attempt - 1])));
                }
              }

              // All retries exhausted
              if (!mounted) return;
              authLog('error', 'fetchProfile', { error: `All ${MAX_PROFILE_RETRIES} retries exhausted` });

              if (profileRef.current) {
                setProfileStatus('ready');
              } else {
                setProfileStatus('error');
                setProfileError(new Error('Profile fetch failed after retries'));
              }
            })().catch(err => {
              if (!mounted) return;
              console.error('[Auth] Profile retry loop error:', err);
              if (!profileRef.current) {
                setProfileStatus('error');
                setProfileError(err instanceof Error ? err : new Error('Profile fetch failed'));
              }
            });
          }

          return; // finally block sets loading=false, initialized=true
        }

        // --- Non-bootstrapped: get session with retry ---
        const MAX_SESSION_RETRIES = 1;
        const SESSION_TIMEOUT_MS = 10000;
        let sessionData: { session: Session | null } | null = null;

        for (let attempt = 1; attempt <= MAX_SESSION_RETRIES; attempt++) {
          const start = Date.now();
          let timeoutId: ReturnType<typeof setTimeout> | null = null;
          try {
            const sessionPromise = authClient.auth.getSession();
            const timeoutPromise = new Promise<never>((_, reject) => {
              timeoutId = setTimeout(() => reject(new Error('Session timeout')), SESSION_TIMEOUT_MS);
            });

            const { data } = await Promise.race([sessionPromise, timeoutPromise]);
            if (timeoutId) clearTimeout(timeoutId);
            sessionData = data;
            break; // Success
          } catch (err) {
            if (timeoutId) clearTimeout(timeoutId);
            authLog('warn', 'getSession', {
              attempt,
              timeoutMs: SESSION_TIMEOUT_MS,
              durationMs: Date.now() - start,
              error: err,
            });
            if (attempt < MAX_SESSION_RETRIES) {
              await new Promise(r => setTimeout(r, jitteredDelay(RETRY_DELAYS_MS[attempt - 1])));
            }
          }
        }

        if (!mounted) return;

        if (!sessionData) {
          // All session retries exhausted — high-signal diagnostic log
          authLog('error', 'getSession', {
            error: `All ${MAX_SESSION_RETRIES} retries exhausted`,
            reason: 'getSession_slow',
            hadCachedProfile: !!profileRef.current,
            hadBootstrappedSession: !!sessionRef.current,
            hadBootstrappedUser: !!userRef.current,
          });
          // If ANY bootstrapped local state exists (session, user, or profile from localStorage),
          // keep all state and schedule background recovery. Only clear when truly nothing exists.
          if (sessionRef.current) {
            needsSessionRecoveryRef.current = true;
            // If no profile yet, fire one immediate non-blocking fetch so profileStatus
            // reaches a terminal state ('ready'/'missing'/'error') and the loading gate clears.
            // Background recovery (visibility/online) handles session; this handles profile.
            if (!profileRef.current && userRef.current) {
              fetchProfile(userRef.current.id, abortController.signal).then(async (result) => {
                if (!mounted) return;
                if (result.status === 'ready') {
                  if (result.profile.is_suspended) { await handleSuspendedProfile(); return; }
                  setProfile(result.profile);
                  setProfileStatus('ready');
                  setProfileError(null);
                  if (userRef.current) setCachedAuth(userRef.current, result.profile);
                } else if (result.status === 'missing') {
                  setProfile(null);
                  setProfileStatus('missing');
                  setProfileError(null);
                  clearCachedAuth();
                } else {
                  setProfileStatus('error');
                  setProfileError(result.error);
                }
              }).catch((err) => {
                if (!mounted) return;
                setProfileStatus('error');
                setProfileError(err instanceof Error ? err : new Error('Profile fetch failed after session timeout'));
              });
            }
          } else {
            setUser(null);
            setProfile(null);
            setProfileStatus('idle');
            setProfileError(null);
          }
          return;
        }

        const session = sessionData.session;

        if (!session?.user) {
          // If a real SDK session exists, preserve it. SDK returning session:null
          // may be transient (network issue during token refresh). Don't log user out.
          // Do NOT call setSession(null) here — keep existing React session state intact.
          if (sessionRef.current) {
            needsSessionRecoveryRef.current = true;
            // If no profile yet, fire one immediate non-blocking fetch so profileStatus
            // reaches a terminal state ('ready'/'missing'/'error') and the loading gate clears.
            if (!profileRef.current && userRef.current) {
              fetchProfile(userRef.current.id, abortController.signal).then(async (result) => {
                if (!mounted) return;
                if (result.status === 'ready') {
                  if (result.profile.is_suspended) { await handleSuspendedProfile(); return; }
                  setProfile(result.profile);
                  setProfileStatus('ready');
                  setProfileError(null);
                  if (userRef.current) setCachedAuth(userRef.current, result.profile);
                } else if (result.status === 'missing') {
                  setProfile(null);
                  setProfileStatus('missing');
                  setProfileError(null);
                  clearCachedAuth();
                } else {
                  setProfileStatus('error');
                  setProfileError(result.error);
                }
              }).catch((err) => {
                if (!mounted) return;
                setProfileStatus('error');
                setProfileError(err instanceof Error ? err : new Error('Profile fetch failed after session-null preserve'));
              });
            }
            return;
          }
          setSession(null);
          setUser(null);
          setProfile(null);
          setProfileStatus('idle');
          setProfileError(null);
          clearCachedAuth();
          return;
        }

        setSession(session);
        setUser(session.user);
        hasAcceptedSessionRef.current = true;

        // --- Background profile fetch with retry (non-blocking) ---
        const hadCachedProfile = !!profileRef.current;
        if (!hadCachedProfile) {
          setProfileStatus('loading');
        }

        // Fire-and-forget: retry up to 3 times, don't block initialization
        const MAX_PROFILE_RETRIES = 3;
        (async () => {
          for (let attempt = 1; attempt <= MAX_PROFILE_RETRIES; attempt++) {
            if (!mounted) return;
            const start = Date.now();
            const result = await fetchProfile(session.user.id, abortController.signal);

            if (result.status === 'ready') {
              if (!mounted) return;
              if (result.profile.is_suspended) { await handleSuspendedProfile(); return; }
              setProfile(result.profile);
              setProfileStatus('ready');
              setProfileError(null);
              setCachedAuth(session.user, result.profile);
              return;
            }

            if (result.status === 'missing') {
              if (!mounted) return;
              setProfile(null);
              setProfileStatus('missing');
              setProfileError(null);
              clearCachedAuth();
              return;
            }

            // Error — log and retry
            authLog('warn', 'fetchProfile', {
              attempt,
              timeoutMs: 8000,
              durationMs: Date.now() - start,
              error: result.error,
            });

            if (attempt < MAX_PROFILE_RETRIES) {
              await new Promise(r => setTimeout(r, jitteredDelay(RETRY_DELAYS_MS[attempt - 1])));
            }
          }

          // All retries exhausted
          if (!mounted) return;
          authLog('error', 'fetchProfile', { error: `All ${MAX_PROFILE_RETRIES} retries exhausted` });

          if (profileRef.current) {
            // Don't brick cached users — keep cached profile visible
            setProfileStatus('ready');
          } else {
            setProfileStatus('error');
            setProfileError(new Error('Profile fetch failed after retries'));
          }
        })().catch(err => {
          if (!mounted) return;
          console.error('[Auth] Profile retry loop error:', err);
          if (!profileRef.current) {
            setProfileStatus('error');
            setProfileError(err instanceof Error ? err : new Error('Profile fetch failed'));
          }
        });
      } catch (err) {
        console.error('[Auth] initAuth unexpected error:', err);
        // Keep cached data if available — don't brick the app
      } finally {
        if (mounted) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    initAuth();

    // Listen for auth changes (AFTER initial check)
    try {
      const { data } = authClient.auth.onAuthStateChange(async (event, eventSession) => {
        // Skip if not initialized yet (initial session handled above)
        if (!mounted) return;

        // CRITICAL: Handle SIGNED_OUT explicitly - this is the ONLY event that should clear auth state.
        // Other events with null eventSession (transient states, ordering issues) must NOT clear state,
        // otherwise the AccountDeactivatedGate will flash and disappear after sign-in.
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setProfile(null);
          setProfileStatus('idle');
          setProfileError(null);
          clearCachedAuth();
          hasAcceptedSessionRef.current = false;
          return;
        }

        // For non-SIGNED_OUT events: if no valid user, ignore and keep existing state.
        // This prevents clearing auth on transient/spurious null sessions.
        if (!eventSession?.user) {
          return;
        }

        // Clear recovery flag: Supabase recovered on its own via auth state change
        needsSessionRecoveryRef.current = false;

        // Handle INITIAL_SESSION conditionally (level-triggered for multi-tab recovery)
        if (event === 'INITIAL_SESSION') {
          // Skip if we already accepted a session AND session state is populated.
          // Edge case: if hasAccepted was set but session state was never updated
          // (e.g., setSession skipped due to timing), accept the event to populate session.
          if (hasAcceptedSessionRef.current && sessionRef.current !== null) return;
          hasAcceptedSessionRef.current = true;
        }

        const isFreshLogin = !userRef.current;
        const isSignInEvent = event === 'SIGNED_IN' || event === 'INITIAL_SESSION';

        // Fresh login from logged-out: make transition ATOMIC
        // Fetch profile BEFORE setting user, so App.tsx never sees user=true + profileStatus=loading
        // Retry up to 3 times with backoff + 10s aggregate timeout (no cache to fall back on for fresh login)
        if (isSignInEvent && isFreshLogin) {
          hasAcceptedSessionRef.current = true;
          reportDeviceSeen(eventSession.access_token, eventSession.user.id);

          const AGGREGATE_TIMEOUT_MS = 10000;
          const MAX_FRESH_RETRIES = 3;
          const aggregateController = new AbortController();
          const aggregateTimeoutId = setTimeout(() => aggregateController.abort(), AGGREGATE_TIMEOUT_MS);
          let aggregateTimedOut = false;

          let lastResult: ProfileFetchResult | null = null;
          for (let attempt = 1; attempt <= MAX_FRESH_RETRIES; attempt++) {
            if (!mounted || aggregateController.signal.aborted) break;
            const start = Date.now();
            try {
              const result = await fetchProfile(eventSession.user.id, aggregateController.signal);
              if (result.status !== 'error') {
                lastResult = result;
                break;
              }
              lastResult = result;
              authLog('warn', 'fetchProfile', {
                attempt,
                timeoutMs: 8000,
                durationMs: Date.now() - start,
                error: result.error,
              });
            } catch (err) {
              authLog('warn', 'fetchProfile', {
                attempt,
                timeoutMs: 8000,
                durationMs: Date.now() - start,
                error: err,
              });
              lastResult = { status: 'error', error: err instanceof Error ? err : new Error('Profile fetch failed') };
            }
            if (attempt < MAX_FRESH_RETRIES && !aggregateController.signal.aborted) {
              await new Promise(r => setTimeout(r, jitteredDelay(RETRY_DELAYS_MS[attempt - 1])));
            }
          }

          clearTimeout(aggregateTimeoutId);

          // Check if aggregate timeout fired (result is error/aborted)
          if (aggregateController.signal.aborted && (!lastResult || lastResult.status === 'error')) {
            aggregateTimedOut = true;
          }

          if (!mounted) return;

          if (aggregateTimedOut) {
            // Aggregate timeout: set session + user IMMEDIATELY so app is functional
            setSession(eventSession);
            setUser(eventSession.user);
            setProfileStatus('loading');

            // Fire-and-forget ONE background profile fetch attempt
            // MUST set terminal profileStatus ('ready', 'missing', or 'error') — never leave stuck on 'loading'
            fetchProfile(eventSession.user.id, abortController.signal).then(async (result) => {
              if (!mounted) return;
              if (result.status === 'ready') {
                if (result.profile.is_suspended) { await handleSuspendedProfile(); return; }
                setProfile(result.profile);
                setProfileStatus('ready');
                setProfileError(null);
                setCachedAuth(eventSession.user, result.profile);
              } else if (result.status === 'missing') {
                setProfile(null);
                setProfileStatus('missing');
                setProfileError(null);
                clearCachedAuth();
              } else {
                setProfileStatus('error');
                setProfileError(result.error);
              }
            }).catch(() => {
              if (!mounted) return;
              setProfileStatus('error');
              setProfileError(new Error('Profile fetch failed after aggregate timeout'));
            });
            return;
          }

          // Atomic: set session + user + profile all in one React batch
          if (lastResult?.status === 'ready' && lastResult.profile.is_suspended) {
            await handleSuspendedProfile();
            return;
          }
          setSession(eventSession);
          setUser(eventSession.user);
          if (lastResult?.status === 'ready') {
            setProfile(lastResult.profile);
            setProfileStatus('ready');
            setProfileError(null);
            setCachedAuth(eventSession.user, lastResult.profile);
          } else if (lastResult?.status === 'missing') {
            setProfile(null);
            setProfileStatus('missing');
            setProfileError(null);
            clearCachedAuth();
          } else {
            setProfileStatus('error');
            setProfileError(lastResult?.status === 'error' ? lastResult.error : new Error('Profile fetch failed after retries'));
          }
          return;
        }

        // Already-logged-in: set session/user immediately (no flash risk)
        setSession(eventSession);
        setUser(eventSession.user);
        hasAcceptedSessionRef.current = true;

        // Background profile refresh for sign-in events (already have cached profile visible)
        if (isSignInEvent) {
          reportDeviceSeen(eventSession.access_token, eventSession.user.id);
          try {
            const result = await fetchProfile(eventSession.user.id, abortController.signal);
            if (mounted) {
              if (result.status === 'ready') {
                if (result.profile.is_suspended) { await handleSuspendedProfile(); return; }
                setProfile(result.profile);
                setProfileStatus('ready');
                setProfileError(null);
                setCachedAuth(eventSession.user, result.profile);
              } else if (result.status === 'missing') {
                setProfile(null);
                setProfileStatus('missing');
                setProfileError(null);
                clearCachedAuth();
              } else {
                // Error: only set error state if no cached profile exists
                if (!profileRef.current) {
                  setProfileStatus('error');
                  setProfileError(result.error);
                }
                // else: keep cached profile visible, log internally
              }
            }
          } catch (err) {
            // Only set error state if no cached profile exists
            if (mounted && !profileRef.current) {
              setProfileStatus('error');
              setProfileError(err instanceof Error ? err : new Error('Profile fetch failed'));
            }
          }
        }
        // For TOKEN_REFRESHED and other events, keep existing profile
      });
      subscription = data.subscription;
    } catch (err) {
      console.error('[Auth] Failed to set up auth listener:', err);
    }

    // --- Background session recovery ---
    // If getSession retries were exhausted but cached auth kept the UI usable,
    // retry once when the tab becomes visible or the device goes back online.
    // Single-flight guard prevents concurrent recovery attempts.
    const trySessionRecovery = async () => {
      if (!needsSessionRecoveryRef.current) return;
      if (recoveryInFlightRef.current) return;
      if (!mounted) return;
      recoveryInFlightRef.current = true;

      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      try {
        const { data } = await Promise.race([
          authClient.auth.getSession(),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error('Recovery timeout')), 10000);
          }),
        ]);
        if (timeoutId) clearTimeout(timeoutId);

        if (!mounted) return;

        if (data.session?.user) {
          needsSessionRecoveryRef.current = false;
          setSession(data.session);
          setUser(data.session.user);
          hasAcceptedSessionRef.current = true;
          // Background profile refresh (fire-and-forget)
          fetchProfile(data.session.user.id, abortController.signal).then(async (result) => {
            if (!mounted) return;
            if (result.status === 'ready') {
              if (result.profile.is_suspended) { await handleSuspendedProfile(); return; }
              setProfile(result.profile);
              setProfileStatus('ready');
              setProfileError(null);
              setCachedAuth(data.session!.user, result.profile);
            }
          });
        }
      } catch {
        // Still wedged — will retry on next visibility/online event
        if (timeoutId) clearTimeout(timeoutId);
      } finally {
        recoveryInFlightRef.current = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') trySessionRecovery();
    };
    const onOnline = () => trySessionRecovery();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    return () => {
      mounted = false;
      abortController.abort();
      subscription?.unsubscribe();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [fetchProfile]);

  const signUp = useCallback(async (email: string, password: string) => {
    const { data, error } = await authClient.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    // Supabase returns a user with empty identities if email already exists
    if (!error && data.user && data.user.identities?.length === 0) {
      return { error: new Error('An account with this email already exists. Please sign in instead.') };
    }
    return { error: error as Error | null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await authClient.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      return { error: error as Error | null };
    }

    const signedInUserId = data.session?.user.id;
    if (!signedInUserId) {
      return { error: null };
    }

    const result = await fetchProfile(signedInUserId);
    if (result.status === 'ready' && result.profile.is_suspended) {
      clearCachedAuth();
      clearBillingSnapshot();
      try { await authClient.auth.signOut(); } catch {}
      return {
        error: new Error('Your account has been closed. Please contact support if you need assistance.'),
      };
    }

    return { error: null };
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    // Clear cache immediately for instant UI update
    clearCachedAuth();
    clearBillingSnapshot();
    await authClient.auth.signOut();
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const oauthRedirectUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    const { error } = await authClient.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: oauthRedirectUrl,
      },
    });
    return { error: error as Error | null };
  }, []);

  const signInWithApple = useCallback(async () => {
    const oauthRedirectUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    const { error } = await authClient.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: oauthRedirectUrl,
      },
    });
    return { error: error as Error | null };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await authClient.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
    });
    return { error: error as Error | null };
  }, []);

  // Derive profileLoading for backward compat
  const profileLoading = profileStatus === 'loading';

  // User needs to set username if:
  // - Authenticated + initialized + not loading + not profileLoading
  // - profileStatus is 'missing' (confirmed no profile row) OR
  // - profileStatus is 'ready' but profile has no username (defensive)
  // - NOT shown on error (show error UI instead)
  const needsUsername = initialized && !loading && !profileLoading && !!user && (
    profileStatus === 'missing' || (profileStatus === 'ready' && !!profile && !profile.username)
  );

  // User needs onboarding if:
  // - Has a username (completed username step)
  // - Onboarding not completed yet
  const needsOnboarding = initialized && !loading && !profileLoading && !!user && !!profile?.username && !profile?.onboarding_completed;

  const value = {
    user,
    session,
    profile,
    loading,
    initialized,
    profileStatus,
    profileError,
    needsUsername,
    needsOnboarding,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    signInWithApple,
    resetPassword,
    setProfileUsername,
    refreshProfile,
    updateProfile,
    completeOnboarding,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
